import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  OutlinedInput,
  TextField,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getBrands, loadBrandsRequest } from '../../../redux/brandsRedux';
import { getUser } from '../../../redux/usersRedux';
import { createLabelSpecification } from '../../../utils/createLabelSpecification';
import { Warehouse } from '@mui/icons-material';
import { addSpecificationRequest } from '../../../redux/specificationRedux';

const DECISION_MAKERS = ['Dyrektor', 'Szef rozlewu'];

const AddSpecification = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const brands = useSelector((state) => getBrands(state));
  const user = useSelector((state) => getUser(state));

  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [decisionMaker, setDecisionMaker] = useState('');
  const [isLineSpecific, setIsLineSpecific] = useState(false);
  const [brandId, setBrandId] = useState([]);
  const [newWarehouse, setNewWarehouse] = useState('');
  const [newLine, setNewLine] = useState('');
  const [newMinXMin, setNewMinXMin] = useState('');
  const [newMinXMax, setNewMinXMax] = useState('');
  const [newMinBMin, setNewMinBMin] = useState('');
  const [newMinBMax, setNewMinBMax] = useState('');
  const [newSpecMin, setNewSpecMin] = useState('');
  const [newSpecNominal, setNewSpecNominal] = useState('');
  const [newSpecMax, setNewSpecMax] = useState('');
  const [newMaxBMin, setNewMaxBMin] = useState('');
  const [newMaxBMax, setNewMaxBMax] = useState('');
  const [newMaxXMin, setNewMaxXMin] = useState('');
  const [newMaxXMax, setNewMaxXMax] = useState('');

  useEffect(() => {
    const start = async () => {
      // Load brands or any other necessary data here
      await dispatch(loadBrandsRequest());
    };
    start();
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const specData = {
      specMin: newSpecMin,
      specNominal: newSpecNominal,
      specMax: newSpecMax,
    };
    const minB = {
      minBMin: newMinBMin,
      minBMax: newMinBMax,
    };
    const minX = {
      minXMin: newMinXMin,
      minXMax: newMinXMax,
    };
    const maxB = {
      maxBMin: newMaxBMin,
      maxBMax: newMaxBMax,
    };
    const maxX = {
      maxXMin: newMaxXMin,
      maxXMax: newMaxXMax,
    };

    const specLabel = createLabelSpecification('nominal', specData);
    const labZoneBMin = createLabelSpecification('minB', minB);
    const labZoneXMin = createLabelSpecification('minX', minX);
    const labZoneBMax = createLabelSpecification('maxB', maxB);
    const labZoneXMax = createLabelSpecification('maxX', maxX);

    const labZones = [
      labZoneBMin,
      labZoneXMin,
      labZoneBMax,
      labZoneXMax,
    ].filter((z) => z != null); // usuwa null i undefined
    // usuwa null i undefined

    let labSpecification = [];

    brandId.map((id) => {
      labSpecification.push({
        brandId: id,
        warehouse: newWarehouse,
        line: newLine,
        specMin: newSpecMin !== '' ? parseFloat(newSpecMin) : null,
        specNominal: newSpecNominal !== '' ? parseFloat(newSpecNominal) : null,
        specMax: newSpecMax !== '' ? parseFloat(newSpecMax) : null,
        specLabel,
        labZone: labZones,
      });
    });

    const data = {
      name,
      unit,
      decisionMaker,
      isLineSpecific,
      labSpecification,
    };

    const res = await dispatch(addSpecificationRequest(data));
    console.log('Added specification:', res);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <h1>{t('masterData.specification.addNewSpecification')}</h1>
      <Divider sx={{ width: '100%', mb: 5, mt: 5, bgcolor: '#C09A53' }} />
      <Box>
        <TextField
          sx={{ mr: 2, mb: 2, width: '40%' }}
          label={t('masterData.description')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          sx={{ mr: 2, mb: 2 }}
          label={t('masterData.specification.unit')}
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
        />

        <Autocomplete
          disablePortal
          sx={{ width: '500px', height: '70px' }}
          limitTags={2}
          options={DECISION_MAKERS}
          onChange={(e, values) => setDecisionMaker(values)}
          renderInput={(params) => (
            <TextField
              sx={{ height: '70px' }}
              {...params}
              label={t('masterData.specification.decisionMaker')}
            />
          )}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isLineSpecific}
              onChange={(e) => setIsLineSpecific(e.target.checked)}
            />
          }
          sx={{ mt: 1 }}
          label={t('masterData.specification.isLineSpecific')}
        />
      </Box>

      <Divider sx={{ width: '100%', mb: 5, mt: 5, bgcolor: '#C09A53' }} />

      <h2>Wybierz gatunek dla specyfikacji</h2>
      <Autocomplete
        disablePortal
        multiple
        sx={{ width: '700px', height: '70px', mb: 3 }}
        limitTags={2}
        options={brands.map((el) => ({
          id: el.id,
          label: el.description,
        }))}
        onChange={(e, values) => setBrandId(values.map((v) => v.id))}
        renderInput={(params) => (
          <TextField
            sx={{ height: '70px' }}
            {...params}
            label={t('masterData.brand')}
          />
        )}
      />
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <FormControl
          sx={{ width: '300px', height: '70px' }}
          size="medium"
          variant="outlined"
          disabled={!isLineSpecific}
        >
          <InputLabel shrink>{t('masterData.specification.whLine')}</InputLabel>
          <OutlinedInput
            sx={{ height: '70px' }}
            notched
            label={t('masterData.specification.whLine')}
            startAdornment={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={t('masterData.warehouse')}>
                  <TextField
                    sx={{ width: '135px' }}
                    disabled={!isLineSpecific}
                    size="small"
                    label={t('masterData.warehouse')}
                    value={newWarehouse}
                    onChange={(e) => setNewWarehouse(e.target.value)}
                  />
                </Tooltip>
                <Tooltip title={t('masterData.line')}>
                  <TextField
                    sx={{ width: '135px' }}
                    disabled={!isLineSpecific}
                    size="small"
                    label={t('masterData.line')}
                    value={newLine}
                    onChange={(e) => setNewLine(e.target.value)}
                  />
                </Tooltip>
              </Box>
            }
            inputProps={{ readOnly: true }}
          />
        </FormControl>
        <FormControl
          sx={{ width: '250px', height: '70px' }}
          size="medium"
          variant="outlined"
        >
          <InputLabel shrink>{t('masterData.specification.minX')}</InputLabel>
          <OutlinedInput
            sx={{ height: '70px' }}
            notched
            label={t('masterData.specification.minX')}
            startAdornment={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  sx={{ width: '110px' }}
                  size="small"
                  label={t('masterData.specification.minValueX')}
                  value={newMinXMin}
                  onChange={(e) => setNewMinXMin(e.target.value)}
                />

                <TextField
                  sx={{ width: '110px' }}
                  size="small"
                  label={t('masterData.specification.maxValueX')}
                  value={newMinXMax}
                  onChange={(e) => setNewMinXMax(e.target.value)}
                />
              </Box>
            }
            inputProps={{ readOnly: true }}
          />
        </FormControl>
        <FormControl
          sx={{ width: '250px', height: '70px' }}
          size="medium"
          variant="outlined"
        >
          <InputLabel shrink>{t('masterData.specification.minB')}</InputLabel>
          <OutlinedInput
            sx={{ height: '70px' }}
            notched
            label={t('masterData.specification.minB')}
            startAdornment={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  sx={{ width: '110px' }}
                  size="small"
                  label={t('masterData.specification.minValueB')}
                  value={newMinBMin}
                  onChange={(e) => setNewMinBMin(e.target.value)}
                />

                <TextField
                  sx={{ width: '110px' }}
                  size="small"
                  label={t('masterData.specification.maxValueB')}
                  value={newMinBMax}
                  onChange={(e) => setNewMinBMax(e.target.value)}
                />
              </Box>
            }
            inputProps={{ readOnly: true }}
          />
        </FormControl>
        <FormControl
          sx={{ width: '405px', height: '70px' }}
          size="medium"
          variant="outlined"
        >
          <InputLabel shrink>
            {t('masterData.specification.defaultValue')}
          </InputLabel>
          <OutlinedInput
            sx={{ height: '70px' }}
            notched
            label={t('masterData.specification.defaultValue')}
            startAdornment={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  sx={{ width: '120px' }}
                  size="small"
                  label={t('masterData.specification.minDefaultValue')}
                  value={newSpecMin}
                  onChange={(e) => setNewSpecMin(e.target.value)}
                />
                <TextField
                  sx={{ width: '120px' }}
                  size="small"
                  label={t('masterData.specification.nominalDefaultValue')}
                  value={newSpecNominal}
                  onChange={(e) => setNewSpecNominal(e.target.value)}
                />

                <TextField
                  sx={{ width: '120px' }}
                  size="small"
                  label={t('masterData.specification.maxDefaultValue')}
                  value={newSpecMax}
                  onChange={(e) => setNewSpecMax(e.target.value)}
                />
              </Box>
            }
            inputProps={{ readOnly: true }}
          />
        </FormControl>
        <FormControl
          sx={{ width: '250px', height: '70px' }}
          size="medium"
          variant="outlined"
        >
          <InputLabel shrink>{t('masterData.specification.maxB')}</InputLabel>
          <OutlinedInput
            sx={{ height: '70px' }}
            notched
            label={t('masterData.specification.maxB')}
            startAdornment={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  sx={{ width: '110px' }}
                  size="small"
                  label={t('masterData.specification.minValueB')}
                  value={newMaxBMin}
                  onChange={(e) => setNewMaxBMin(e.target.value)}
                />

                <TextField
                  sx={{ width: '110px' }}
                  size="small"
                  label={t('masterData.specification.maxValueB')}
                  value={newMaxBMax}
                  onChange={(e) => setNewMaxBMax(e.target.value)}
                />
              </Box>
            }
            inputProps={{ readOnly: true }}
          />
        </FormControl>
        <FormControl
          sx={{ width: '250px', height: '70px' }}
          size="medium"
          variant="outlined"
        >
          <InputLabel shrink>{t('masterData.specification.maxX')}</InputLabel>
          <OutlinedInput
            sx={{ height: '70px' }}
            notched
            label={t('masterData.specification.maxX')}
            startAdornment={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  sx={{ width: '110px' }}
                  size="small"
                  label={t('masterData.specification.minValueX')}
                  value={newMaxXMin}
                  onChange={(e) => setNewMaxXMin(e.target.value)}
                />

                <TextField
                  sx={{ width: '110px' }}
                  size="small"
                  label={t('masterData.specification.maxValueX')}
                  value={newMaxXMax}
                  onChange={(e) => setNewMaxXMax(e.target.value)}
                />
              </Box>
            }
            inputProps={{ readOnly: true }}
          />
        </FormControl>
      </Box>
      <Button
        type="submit"
        variant="contained"
        sx={{ bgcolor: '#C09A53', ':hover': { bgcolor: '#9c7c43' }, mt: 1 }}
      >
        {t('masterData.button.add')}
      </Button>
    </Box>
  );
};

export default AddSpecification;
